import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface ExternalUserCookiePayload {
  id: string;
  name: string;
  email: string;
  image?: string;
  accessToken?: string;
  idToken?: string;
  provider: string;
  comtrakAuthToken?: string;
}

interface ComtrakHandoffCookiePayload {
  user?: ExternalUserCookiePayload;
  authToken?: string;
  issuedAt?: number;
}

export interface AuthSessionState {
  isAuthenticated: boolean;
  token?: string;
  refreshToken?: string;
  userName?: string;
  userEmail?: string;
  userImage?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly storageKey = 'auth.session';
  private sessionState: AuthSessionState | null = null;
  private readonly userState$ = new BehaviorSubject<AuthSessionState | null>(null);

  constructor(private http: HttpClient, private router: Router) {
    this.loadSessionFromStorage();
    this.userState$.next(this.sessionState);
  }

  async validateWithExternalLogin(): Promise<boolean> {
    return this.validateWithExternalLoginInternal(true);
  }

  async recoverAuthentication(): Promise<boolean> {
    if (await this.refreshToken()) {
      return true;
    }
    return this.validateWithExternalLoginInternal(false);
  }

  private async validateWithExternalLoginInternal(allowRefresh: boolean): Promise<boolean> {
    // If we already have a refresh token, use it instead of external-login
    const existingRefreshToken = this.getRefreshToken();
    if (allowRefresh && existingRefreshToken) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return true;
      }
    }

    // No refresh token, proceed with external-login
    const cookiePayload = this.readExternalCookie();
    if (!cookiePayload) {
      console.warn('[Auth] Missing or unreadable user cookie. Skipping ExternalLogin.');
      this.clearSession();
      return false;
    }

    try {
      const response: any = await firstValueFrom(
        this.http.post(
          environment.comforceURL+ '/auth/external-login',
          cookiePayload,
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              'Accept': 'application/json, text/plain'
            })
          }
        )
      );
      console.log('[Auth] ExternalLogin response:', response);

      // Extract tokens and user info from the new API response format
      const accessToken: string | undefined = response?.access_token;
      const refreshToken: string | undefined = response?.refresh_token;
      const user = response?.user;
      
      if (!accessToken || !user) {
        console.warn('[Auth] ExternalLogin response missing required fields (access_token or user).');
        this.clearSession();
        return false;
      }

      const userName: string | undefined = user?.name || cookiePayload.name;
      const userEmail: string | undefined = user?.email || cookiePayload.email;
      const userImage: string | undefined = user?.image || cookiePayload.image;

      this.setSession({ 
        isAuthenticated: true, 
        token: accessToken, 
        refreshToken: refreshToken,
        userName, 
        userEmail, 
        userImage 
      });
      console.log('[Auth] Session set:', { 
        isAuthenticated: true, 
        hasToken: !!accessToken, 
        hasRefreshToken: !!refreshToken,
        userName, 
        userEmail 
      });
      return true;
    } catch (err) {
      console.error('[Auth] ExternalLogin error:', err);
      this.clearSession();
      return false;
    }
  }

  async refreshToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.warn('[Auth] No refresh token available.');
      this.clearSession();
      return false;
    }

    try {
      const response: any = await firstValueFrom(
        this.http.post(
          environment.comforceURL + '/auth/refresh',
          { refresh_token: refreshToken },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            })
          }
        )
      );
      console.log('[Auth] Refresh token response:', response);

      // Extract tokens from the response
      const accessToken: string | undefined = response?.access_token;
      const newRefreshToken: string | undefined = response?.refresh_token;

      if (!accessToken) {
        console.warn('[Auth] Refresh token response missing access_token.');
        this.clearSession();
        return false;
      }

      // Update session with new tokens while preserving user info
      const currentState = this.sessionState || { isAuthenticated: false };
      this.setSession({
        ...currentState,
        isAuthenticated: true,
        token: accessToken,
        refreshToken: newRefreshToken || refreshToken, // Use new refresh token if provided, otherwise keep the old one
      });

      console.log('[Auth] Token refreshed successfully:', { 
        hasToken: !!accessToken, 
        hasRefreshToken: !!newRefreshToken 
      });
      return true;
    } catch (err) {
      console.error('[Auth] Refresh token error:', err);
      this.clearSession();
      return false;
    }
  }

  isAuthenticated(): boolean {
    return this.sessionState?.isAuthenticated === true && !!this.sessionState?.token;
  }

  getToken(): string | undefined {
    return this.sessionState?.token;
  }

  getRefreshToken(): string | undefined {
    return this.sessionState?.refreshToken;
  }

  user$() {
    return this.userState$.asObservable();
  }

  clearSession(): void {
    this.sessionState = { isAuthenticated: false };
    sessionStorage.removeItem(this.storageKey);
    this.userState$.next(this.sessionState);
  }

  async logout(): Promise<void> {
    // Call logout API if refresh token exists
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      try {
        await firstValueFrom(
          this.http.post(
            environment.comforceURL + '/auth/logout',
            { refresh_token: refreshToken },
            {
              headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              })
            }
          )
        );
        console.log('[Auth] Logout API called successfully');
      } catch (err) {
        // Log error but continue with local logout
        console.error('[Auth] Logout API error:', err);
      }
    }

    // Clear the user cookie by setting it to expire
    // Cookie is set by Comtrak as: Domain=.dmclinical.com; Path=/; Secure; SameSite=None
    // To clear it, we must match these exact attributes
    const cookieName = environment.externalAuthCookieName;
    const hostname = window.location.hostname;
    const expireDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    
    // Extract parent domain (e.g., dmclinical.com from comforce.dev.dmclinical.com)
    const domainParts = hostname.split('.');
    let parentDomain = '';
    if (domainParts.length >= 2) {
      // Get the last two parts (e.g., dmclinical.com)
      parentDomain = domainParts.slice(-2).join('.');
    }
    
    // Primary attempt: Match the exact cookie attributes as set by Comtrak
    // Cookie format: Domain=.dmclinical.com; Path=/; Secure; SameSite=None
    const clearAttempts = [
      // Exact match for Comtrak cookie: Domain=.dmclinical.com; Path=/; Secure; SameSite=None
      parentDomain ? `${cookieName}=; expires=${expireDate}; path=/; domain=.${parentDomain}; Secure; SameSite=None;` : '',
      // Also try with Max-Age=0 (alternative way to expire)
      parentDomain ? `${cookieName}=; Max-Age=0; path=/; domain=.${parentDomain}; Secure; SameSite=None;` : '',
      
      // Fallback attempts with variations
      parentDomain ? `${cookieName}=; expires=${expireDate}; path=/; domain=.${parentDomain}; Secure;` : '',
      parentDomain ? `${cookieName}=; expires=${expireDate}; path=/; domain=${parentDomain}; Secure; SameSite=None;` : '',
      `${cookieName}=; expires=${expireDate}; path=/; Secure; SameSite=None;`,
      `${cookieName}=; expires=${expireDate}; path=/;`,
    ].filter(attempt => attempt !== '');
    
    // Execute all clear attempts
    clearAttempts.forEach(attempt => {
      try {
        document.cookie = attempt;
        console.log('[Auth] Cookie clear attempt:', attempt);
      } catch (e) {
        // Silently fail if domain is invalid
        console.debug('[Auth] Cookie clear attempt failed:', attempt, e);
      }
    });
    
    console.log('[Auth] Attempted to clear cookie:', cookieName, 'on domain:', parentDomain ? `.${parentDomain}` : 'current domain');
    
    // Clear session
    this.clearSession();
    
    // Navigate to auth-required page
    this.router.navigateByUrl('/auth-required');
  }

  private setSession(state: AuthSessionState): void {
    this.sessionState = state;
    sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    this.userState$.next(this.sessionState);
  }

  private loadSessionFromStorage(): void {
    const raw = sessionStorage.getItem(this.storageKey);
    if (raw) {
      try {
        this.sessionState = JSON.parse(raw) as AuthSessionState;
      } catch {
        this.sessionState = { isAuthenticated: false };
      }
    } else {
      this.sessionState = { isAuthenticated: false };
    }
  }

  readExternalCookie(): ExternalUserCookiePayload | null {
    const cookieName = environment.externalAuthCookieName;
    const rawValue = this.getCookie(cookieName);
    if (!rawValue) return null;

    // Try parsing as-is, then URL-decoded, then with surrounding quotes removed
    const candidates: string[] = [
      rawValue,
      safeDecode(rawValue),
    ];

    // Some environments wrap JSON cookie values in quotes
    if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
      const unquoted = rawValue.slice(1, -1);
      candidates.push(unquoted, safeDecode(unquoted));
    }

    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate) as ExternalUserCookiePayload | ComtrakHandoffCookiePayload;
        const normalized = this.normalizeExternalCookiePayload(parsed);
        if (normalized) return normalized;
      } catch {}
    }
    return null;
  }

  private normalizeExternalCookiePayload(payload: ExternalUserCookiePayload | ComtrakHandoffCookiePayload | null): ExternalUserCookiePayload | null {
    if (!payload) return null;

    const handoffPayload = payload as ComtrakHandoffCookiePayload;
    if (handoffPayload.user?.id && handoffPayload.user?.email) {
      return {
        ...handoffPayload.user,
        provider: handoffPayload.user.provider || 'google',
        comtrakAuthToken: handoffPayload.authToken
      };
    }

    const legacyPayload = payload as ExternalUserCookiePayload;
    if (legacyPayload.id && legacyPayload.email) {
      return legacyPayload;
    }

    return null;
  }

  private getCookie(name: string): string | null {
    const nameEq = name + '=';
    const parts = document.cookie.split(';');
    for (let part of parts) {
      part = part.trim();
      if (part.indexOf(nameEq) === 0) {
        const value = part.substring(nameEq.length);
        return value;
      }
    }
    return null;
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}


